import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Check, Star, Users, MessageCircle, Zap, BarChart3, Clock, Brain, Sparkles, Play, TrendingUp, Shield, Globe, Palette, Database } from 'lucide-react';
import mockData from '../mock';
import SEOHead from '../components/SEOHead';

const Home = () => {
  const [currentStat, setCurrentStat] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % mockData.stats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const IconComponent = ({ name, className }) => {
    const icons = {
      Brain, Clock, Zap, BarChart3, Users, MessageCircle, Shield, Globe, Palette, Database
    };
    const Icon = icons[name] || Brain;
    return <Icon className={className} />;
  };

  const enhancedFeatures = [
    ...mockData.features,
    {
      id: 7,
      title: "Reliable Security",
      description: "Your data is protected with standard security measures and best practices for safe operation.",
      icon: "Shield",
      benefits: ["Data encryption", "Secure connections", "Privacy protection"]
    },
    {
      id: 8,
      title: "Smart Data Management",
      description: "We store your data wisely and provide you with valuable statistics and insights about your customer interactions.",
      icon: "Database",
      benefits: ["Intelligent data storage", "Conversation analytics", "Usage statistics", "Performance insights"]
    }
  ];

  return (
    <div className="pt-16 bg-gray-950 overflow-hidden">
      <SEOHead 
        title="BrixBot - AI Customer Service Chatbots" 
        description="Transform your customer service with AI chatbots trained specifically on your business data. Provide instant, accurate responses 24/7 with BrixBot."
        isIndexable={true}
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        {/* Enhanced Background with Parallax */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 via-violet-950/40 to-purple-950/30"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        />
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        
        {/* Animated Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-violet-500/20 via-purple-500/20 to-fuchsia-500/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-12">
            <div className="animate-fade-in-up">
              <Badge className="glass-effect text-violet-300 border-violet-500/30 hover:bg-violet-600/30 transition-all duration-500 text-lg py-3 px-8 animate-neon-pulse">
                <Sparkles className="w-5 h-5 mr-2" />
                Now Available for Small & Medium Businesses
              </Badge>
            </div>
            
            <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold text-white leading-tight animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Intelligent Customer Service
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 via-fuchsia-400 to-pink-400 animate-text-glow">
                Powered by Your Data
              </span>
            </h1>
            
            <p className="text-2xl md:text-3xl text-gray-300 max-w-5xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              Transform your customer service with AI chatbots trained specifically on your business data. 
              Provide instant, accurate responses 24/7 with <span className="text-violet-400 font-semibold text-shadow-glow">BrixBot</span>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-8 justify-center items-center mt-16 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 hover:from-violet-700 hover:via-purple-700 hover:to-fuchsia-700 text-white px-14 py-8 text-xl rounded-3xl shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-500 transform hover:scale-105 relative overflow-hidden group focus-ring"
                >
                  <span className="relative z-10 flex items-center">
                    Get Your Chatbot Now
                    <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Button>
              </Link>
              
              <Link to="/demo">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="glass-effect text-violet-300 hover:bg-violet-600/20 hover:border-violet-400/60 px-14 py-8 text-xl rounded-3xl backdrop-blur-sm transition-all duration-500 group focus-ring hover-lift"
                >
                  <Play className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-300" />
                  Watch Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Stats Section */}
      <section className="py-32 bg-gray-900/50 backdrop-blur-sm relative" id="stats" data-animate>
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 via-purple-500/8 to-fuchsia-500/5" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-20 transition-all duration-1000 ${isVisible.stats ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-8 text-shadow-glow">
              Trusted by Industry Leaders
            </h2>
            <p className="text-2xl text-gray-300">
              Join businesses already using BrixBot
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockData.stats.map((stat, index) => (
              <div key={index} className={`text-center group relative transition-all duration-700 ${isVisible.stats ? 'animate-scale-in' : 'opacity-0'}`} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className={`transition-all duration-500 ${currentStat === index ? 'scale-110' : 'scale-100'}`}>
                  <div className="text-6xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 via-fuchsia-400 to-pink-400 mb-4 group-hover:scale-110 transition-transform duration-300 animate-text-glow">
                    {stat.value || '✓'}
                  </div>
                  <div className="text-gray-400 text-lg md:text-xl font-medium">
                    {stat.label}
                  </div>
                  {currentStat === index && (
                    <div className="absolute -inset-6 bg-gradient-to-r from-violet-500/30 via-purple-500/25 to-fuchsia-500/20 rounded-2xl blur-2xl animate-neon-pulse" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section className="py-32 bg-gray-950 relative overflow-hidden" id="features" data-animate>
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/8 via-purple-500/10 to-fuchsia-500/5" />
        <div className="absolute inset-0 bg-grid-pattern opacity-10" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-24 transition-all duration-1000 ${isVisible.features ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <Badge className="glass-effect text-violet-300 border-violet-500/30 mb-8 text-lg py-3 px-6">
              <Sparkles className="w-5 h-5 mr-2" />
              Advanced Features
            </Badge>
            <h2 className="text-6xl md:text-7xl font-bold text-white mb-10 text-shadow-glow">
              Why Choose BrixBot?
            </h2>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Powered by cutting-edge AI and trained on your specific business data
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {enhancedFeatures.map((feature, index) => (
              <Card 
                key={feature.id} 
                className={`card-premium hover-lift transition-all duration-700 ${isVisible.features ? 'animate-scale-in' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/8 via-purple-500/5 to-fuchsia-500/3 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <CardHeader className="space-y-6 relative z-10">
                  <div className="relative">
                    <div className="w-18 h-18 bg-gradient-to-br from-violet-500 via-purple-600 to-fuchsia-600 rounded-3xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-2xl shadow-violet-500/25 animate-neon-pulse">
                      <IconComponent name={feature.icon} className="w-9 h-9 text-white" />
                    </div>
                  </div>
                  <CardTitle className="text-white text-xl font-bold">{feature.title}</CardTitle>
                </CardHeader>
                
                <CardContent className="relative z-10 space-y-6">
                  <CardDescription className="text-gray-300 text-base leading-relaxed">
                    {feature.description}
                  </CardDescription>
                  
                  <div className="space-y-3">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-violet-500/20 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-violet-400" />
                        </div>
                        <span className="text-gray-400 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className={`text-center mt-20 transition-all duration-1000 ${isVisible.features ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.5s' }}>
            <Link to="/features">
              <Button 
                variant="outline" 
                size="lg"
                className="glass-effect text-violet-300 hover:bg-violet-600/20 hover:border-violet-400/60 px-12 py-6 text-lg rounded-3xl backdrop-blur-sm transition-all duration-500 group focus-ring hover-lift"
              >
                Explore All Features
                <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trusted by Growing Businesses Statement */}
      <section className="py-32 bg-gray-900/30 backdrop-blur-sm" id="trusted" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center transition-all duration-1000 ${isVisible.trusted ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-6xl md:text-7xl font-bold text-white mb-10 text-shadow-glow">
              Trusted by Growing Businesses
            </h2>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-32 bg-gradient-to-r from-violet-900/40 via-purple-900/35 to-fuchsia-900/25 relative overflow-hidden" id="cta" data-animate>
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/15 via-purple-500/12 to-fuchsia-500/8" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className={`transition-all duration-1000 ${isVisible.cta ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <Badge className="glass-effect text-violet-300 border-violet-500/30 mb-10 text-xl py-3 px-8 animate-neon-pulse">
              <Sparkles className="w-6 h-6 mr-2" />
              Ready to Transform?
            </Badge>
            
            <h2 className="text-6xl md:text-7xl font-bold text-white mb-10 text-shadow-glow">
              Ready to Transform Your
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 via-fuchsia-400 to-pink-400 animate-text-glow">
                Customer Service?
              </span>
            </h2>
            
            <p className="text-2xl text-gray-300 mb-16 max-w-4xl mx-auto leading-relaxed">
              Join businesses already using BrixBot to provide instant, accurate customer support.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-8 justify-center">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 hover:from-violet-700 hover:via-purple-700 hover:to-fuchsia-700 text-white px-14 py-8 text-2xl rounded-3xl shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-500 transform hover:scale-105 relative overflow-hidden group focus-ring"
                >
                  <span className="relative z-10 flex items-center">
                    <Sparkles className="w-6 h-6 mr-3" />
                    Start Your Setup
                  </span>
                </Button>
              </Link>
              
              <Link to="/contact">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="glass-effect text-violet-300 hover:bg-violet-600/20 hover:border-violet-400/60 px-14 py-8 text-2xl rounded-3xl backdrop-blur-sm transition-all duration-500 group focus-ring hover-lift"
                >
                  <Users className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-300" />
                  Talk to Sales
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;